import {
  createResourceMaterial,
  MaterialType,
  updateResourceMaterial,
} from '@/api/resourceMaterial';
import UploadImage from '@/components/UploadImage';
import { FORMAT_TIME } from '@/constants/date';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
} from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import LinkSelectForm from '@/components/LinkSelectForm'; // 最新封装好的组件
import { ActivityNotifyType } from '@/api/activityMng'; // 定义了ActivityNotifyType枚举

interface MaterialFormProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  resourceCode?: string;
  initialValues?: any;
  mode: 'create' | 'edit';
  id?: number;
}

const MaterialForm: React.FC<MaterialFormProps> = ({
  open,
  onCancel,
  onSuccess,
  resourceCode,
  initialValues,
  mode,
  id,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [materialType, setMaterialType] = useState<MaterialType>(
    initialValues?.materialType ?? MaterialType.Single
  );

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        materialType: initialValues?.materialType ?? MaterialType.Single,
        sort: initialValues?.sort ?? 0,
        ...initialValues,
        positionMaterialImageResourceDTOList: (
          initialValues?.positionMaterialImageResourceDTOList ?? [
            { imageUrl: '', openType: ActivityNotifyType.LINK, openUrl: '' },
          ]
        ).map((item: any) => ({
          ...item,
          linkData: {
            notifyType: item.openType,
            link: item.openUrl,
          },
        })),
      });
      setMaterialType(initialValues?.materialType ?? MaterialType.Single);
    }
  }, [initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // 映射数据：将 linkData.notifyType, linkData.link 转换为 openType, openUrl
      const mappedValues = {
        ...values,
        positionMaterialImageResourceDTOList: values.positionMaterialImageResourceDTOList.map((item: any) => ({
          ...item,
          openType: Number(item.linkData.notifyType),
          openUrl: item.linkData.link,
        })),
      };

      // 移除 linkData
      mappedValues.positionMaterialImageResourceDTOList.forEach((item: any) => {
        delete item.linkData;
      });

      Modal.confirm({
        title: mode === 'create' ? '确认提交' : '确认修改',
        content:
          mode === 'create'
            ? '确认创建该素材吗？创建后素材将自动上线。'
            : '确认修改该素材吗？',
        onOk: async () => {
          try {
            setLoading(true);
            const payload = {
              ...mappedValues,
              code: resourceCode,
              status: mode === 'create' ? 0 : undefined,
              startTime: values.timeRange[0].format(FORMAT_TIME),
              endTime: values.timeRange[1].format(FORMAT_TIME),
            };
            delete payload.timeRange;
            if (mode === 'create') {
              await createResourceMaterial(resourceCode || '', payload);
              messageApi.success('创建成功');
            } else {
              if (!id) {
                throw new Error('缺少素材ID');
              }
              await updateResourceMaterial(resourceCode || '', {
                ...payload,
                id,
              });
              messageApi.success('修改成功');
            }
            onSuccess();
            form.resetFields();
          } catch (error: unknown) {
            messageApi.error(
              error instanceof Error
                ? error.message
                : mode === 'create'
                  ? '创建失败'
                  : '修改失败'
            );
          } finally {
            setLoading(false);
          }
        },
      });
    } catch (error: unknown) {
      messageApi.error('请检查表单填写是否正确');
    }
  };

  const handleTypeChange = (e: any) => {
    const newType = e.target.value;
    if (materialType !== newType) {
      setMaterialType(newType);
      form.setFieldsValue({
        positionMaterialImageResourceDTOList:
          newType === MaterialType.Single
            ? [{ imageUrl: '', linkData: { notifyType: ActivityNotifyType.LINK, link: '' } }]
            : [
                { imageUrl: '', linkData: { notifyType: ActivityNotifyType.LINK, link: '' } },
                { imageUrl: '', linkData: { notifyType: ActivityNotifyType.LINK, link: '' } },
              ],
      });
    }
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current < dayjs().startOf('day');
  };

  const disabledTime = (current: dayjs.Dayjs, type: 'start' | 'end') => {
    if (type === 'start' && current && current.isSame(dayjs(), 'day')) {
      const currentHour = dayjs().hour();
      const currentMinute = dayjs().minute();

      return {
        disabledHours: () => Array.from({ length: currentHour }, (_, i) => i),
        disabledMinutes: (selectedHour: number) =>
          selectedHour === currentHour
            ? Array.from({ length: currentMinute }, (_, i) => i)
            : [],
      };
    }
    return {};
  };

  return (
    <>
      {contextHolder}
      <Drawer
        title={mode === 'create' ? '创建资源位素材' : '编辑资源位素材'}
        open={open}
        onClose={onCancel}
        maskClosable={false}
        width={800}
        bodyStyle={{
          padding: '24px',
          height: 'calc(100% - 55px)',
          overflow: 'auto',
        }}
        footer={
          <div
            style={{
              textAlign: 'right',
              position: 'absolute',
              right: 0,
              bottom: 0,
              left: 0,
              borderTop: '1px solid #e8e8e8',
              padding: '12px 24px',
              background: '#fff',
            }}
          >
            <Button onClick={onCancel} style={{ marginRight: 12 }}>
              取消
            </Button>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              确定
            </Button>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            materialType: initialValues?.materialType ?? MaterialType.Single,
            sort: initialValues?.sort ?? 0,
            ...initialValues,
            positionMaterialImageResourceDTOList: (
              initialValues?.positionMaterialImageResourceDTOList ?? [
                { imageUrl: '', openType: ActivityNotifyType.LINK, openUrl: '' },
              ]
            ).map((item: any) => ({
              ...item,
              // 转换成 linkData 结构
              linkData: {
                notifyType: item.openType,
                link: item.openUrl,
              },
            })),
          }}
          style={{ maxWidth: '100%' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Form.Item
              label="素材描述"
              name="materialDesc"
              rules={[{ max: 500, message: '最多500字符' }]}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                placeholder="请输入素材描述"
                maxLength={500}
                showCount
                rows={4}
                style={{ resize: 'none' }}
              />
            </Form.Item>

            <Form.Item
              label="素材类型"
              name="materialType"
              rules={[{ required: true, message: '请选择素材类型' }]}
              style={{ marginBottom: 0 }}
            >
              <Radio.Group onChange={handleTypeChange}>
                <Radio value={MaterialType.Single}>单图</Radio>
                <Radio value={MaterialType.Double}>双图</Radio>
              </Radio.Group>
            </Form.Item>

            <div style={{ display: 'flex', gap: '24px' }}>
              <Form.Item
                label="排序值"
                name="sort"
                rules={[
                  { required: true, message: '请输入排序值' },
                  {
                    type: 'number',
                    min: 0,
                    max: 100,
                    message: '排序值范围为0-100',
                  },
                ]}
                style={{ marginBottom: 0, flex: 1 }}
              >
                <InputNumber
                  min={0}
                  max={100}
                  placeholder="请输入排序值"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                label="展示时间"
                name="timeRange"
                tooltip="结束时间必须晚于开始时间"
                rules={[
                  { required: true, message: '请选择展示时间' },
                  {
                    validator: async (_, value) => {
                      if (value && value[0] && value[1]) {
                        if (value[0].isAfter(value[1])) {
                          throw new Error('结束时间必须晚于开始时间');
                        }
                      }
                    },
                  },
                ]}
                style={{ marginBottom: 0, flex: 2 }}
              >
                <DatePicker.RangePicker
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: '100%' }}
                  disabledDate={disabledDate}
                  disabledTime={disabledTime}
                  placeholder={['开始时间', '结束时间']}
                />
              </Form.Item>
            </div>

            <Form.List name="positionMaterialImageResourceDTOList">
              {(fields) => (
                <div
                  style={{
                    display: 'grid',
                    gap: '24px',
                    gridTemplateColumns:
                      materialType === MaterialType.Double
                        ? 'repeat(2, 1fr)'
                        : '1fr',
                  }}
                >
                  {fields.map((field, index) => {
                    const { key, ...restField } = field;
                    const isDouble = materialType === MaterialType.Double;
                    const imageSizeText = isDouble
                      ? '建议尺寸：528x264'
                      : '建议尺寸：1083x264';

                    return (
                      <div
                        key={key}
                        style={{
                          padding: '24px',
                          background: '#fafafa',
                          borderRadius: '8px',
                        }}
                      >
                        <Form.Item
                          required
                          label={
                            <div>
                              <span>素材图{isDouble ? index + 1 : ''}</span>
                              <span
                                style={{
                                  marginLeft: '8px',
                                  fontSize: '12px',
                                  color: '#999',
                                }}
                              >
                                {imageSizeText}
                              </span>
                            </div>
                          }
                          style={{ marginBottom: 0 }}
                        >
                          <Form.Item
                            {...restField}
                            name={[field.name, 'imageUrl']}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[{ required: true, message: '请上传素材图' }]}
                            noStyle
                          >
                            <UploadImage
                              width={isDouble ? 528 : 1083}
                              height={264}
                            />
                          </Form.Item>
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[field.name, 'linkData']}
                          label="跳转链接"
                          rules={[
                            {
                              required: true,
                              validator: async (_, value) => {
                                if (!value || !value.notifyType || !value.link) {
                                  throw new Error('请选择跳转类型并输入跳转链接');
                                }
                              },
                            },
                          ]}
                          valuePropName="value"
                        >
                          <LinkSelectForm />
                        </Form.Item>
                      </div>
                    );
                  })}
                </div>
              )}
            </Form.List>
          </div>
        </Form>
      </Drawer>
    </>
  );
};

export default MaterialForm;
